import React from 'react';
import { Box, Typography, Container, Button, Chip, Stack } from '@mui/material';
import profileImage from '../assets/srivathsa.jpg';

const contact = {
  email: 'srivathsa08@gmail.com',
  phone: '+91 7382043557',
  phoneHref: 'tel:+917382043557',
  linkedin: 'https://www.linkedin.com/in/srivathsa-kavirayuni-553355120/',
  site: 'https://srivathsakavirayuni.techiegra.com/',
};

const systems = [
  {
    kicker: 'Core platform · async first',
    title: 'Polaris',
    when: 'Jun 2025 — Present · 27+ services, 27 repositories',
    body: 'The job and data plane the products run on. Bulk processing, events, and schedules across a wide AWS surface, built for portfolios past one million records.',
    chips: ['SQS FIFO', 'Lambda + ECS', 'Step Functions', 'EventBridge', 'Aurora', 'DynamoDB', 'Polars'],
  },
  {
    kicker: 'Product · consumes Polaris',
    title: 'DataStudio',
    when: 'Mar 2023 — Present · 7 applications and services',
    body: 'PolarisFE UI, web API, insights runtime, developer kit, publisher, notification service, and the signal bus. Insights are modules any engineer can publish without changing the orchestrator.',
    chips: ['React', 'Redux', 'FastAPI', 'Step Functions', 'AppSync', 'Notifications'],
  },
  {
    kicker: 'Reporting UI · React',
    title: 'Universal Product Platform',
    when: 'Sep 2022 — Mar 2023',
    body: 'Company, risk, and monitoring reports as React modules on Redux. One shell, shared auth and design system, route-level code splitting.',
    chips: ['React', 'Redux', 'RxJS'],
  },
];

const cuts = [
  { title: 'Request path', body: 'API Gateway, JWT or IAM, validate, tenant check, enqueue. No file parsing in the request.' },
  { title: 'Heavy path', body: 'Express FIFO to Lambda. Standard FIFO to ECS. Polars on the file. A dead-letter queue for a human.' },
  { title: 'Shared services', body: 'Nexus chains and schedules, notifications, and AppSync signals. One implementation for every product.' },
  { title: 'State', body: 'Aurora for transactions. DynamoDB for jobs, cache, and replay. S3 for artifacts. SSM and KMS for secrets.' },
];

const earlier = [
  {
    title: 'Kanerika Software — Front End Developer',
    when: 'Sep 2021 — Aug 2022',
    body: 'CCSM. A Zoom plugin captured meeting speech and facial cues. Third-party AI produced sentiment and transcripts. Node.js served them through GraphQL. React rendered the dashboards.',
    stack: 'React · Node.js · GraphQL · AWS',
  },
  {
    title: 'Cognizant — Hybrid Application Developer',
    when: 'Dec 2020 — Sep 2021',
    body: 'Schneider Freight Power. One Ionic React app for Android and iOS: book loads, track shipments, GPS, and capacity. Redux-Saga for async flows. Jest and Cypress for tests.',
    stack: 'Ionic React · Redux-Saga · Capacitor · Jest · Cypress',
  },
  {
    title: 'Creditsafe — Frontend Developer',
    when: 'Jun 2019 — Dec 2021',
    body: 'Universal Product Platform for company search and domestic or international reports. Data Cleaning (2020) for Excel and CSV portfolios. Monitoring and Connect (2018–2019) for portfolio changes and internal API checks.',
    stack: 'React · TypeScript · Redux · Redux Observable · Jest',
  },
];

const stackGroups = [
  { title: 'Platform', items: ['Python', 'FastAPI', 'Polars', 'PostgreSQL', 'DynamoDB', 'HLD / LLD'] },
  { title: 'AWS', items: ['Lambda', 'ECS', 'SQS', 'Step Functions', 'EventBridge', 'API Gateway', 'S3', 'Glue'] },
  { title: 'Product UI', items: ['React', 'TypeScript', 'Redux', 'Redux Observable', 'RxJS', 'Jest'] },
  { title: 'Delivery', items: ['Terraform', 'Azure Pipelines', 'Git', 'CloudWatch', 'X-Ray', 'Cursor'] },
];

const sectionTitleSx = {
  fontSize: '0.78rem',
  letterSpacing: '0.14em',
  textTransform: 'uppercase' as const,
  color: 'primary.main',
  fontWeight: 700,
  mb: 2,
};

const Home: React.FC = () => {
  return (
    <Box id="top" component="main" sx={{ pt: '64px' }}>
      <Box sx={{ bgcolor: '#0e4c5c', color: 'white', py: { xs: 6, md: 8 } }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 180px' }, gap: 4, alignItems: 'center' }}>
            <Box>
              <Typography variant="overline" sx={{ letterSpacing: '0.16em', opacity: 0.85 }}>
                9+ years · Creditsafe since Sep 2022
              </Typography>
              <Typography variant="h1" sx={{ fontSize: { xs: '2.4rem', md: '3.4rem' }, mt: 0.5 }}>
                Srivathsa Kavirayuni
              </Typography>
              <Typography variant="h2" sx={{ fontSize: { xs: '1.25rem', md: '1.5rem' }, fontWeight: 600, mt: 0.5 }}>
                Senior Systems Engineer
              </Typography>
              <Typography sx={{ mt: 2, maxWidth: 680, fontSize: '1.05rem', lineHeight: 1.5 }}>
                I design multi-tenant data platforms that a product organization runs on.
                Request paths stay short. Anything that can run long is a job, a queue, and a tenant check before the read.
              </Typography>
              <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap" sx={{ mt: 3 }}>
                <Button variant="contained" href={`mailto:${contact.email}`} sx={{ bgcolor: 'white', color: '#0e4c5c', '&:hover': { bgcolor: '#e7eef0' } }}>
                  Email
                </Button>
                <Button variant="outlined" color="inherit" href={contact.linkedin} target="_blank" rel="noopener noreferrer">
                  LinkedIn
                </Button>
                <Button variant="outlined" color="inherit" href={contact.phoneHref}>
                  {contact.phone}
                </Button>
              </Stack>
            </Box>
            <Box
              component="img"
              src={profileImage}
              alt="Srivathsa Kavirayuni"
              sx={{
                width: { xs: 120, md: 168 },
                height: { xs: 120, md: 168 },
                borderRadius: '50%',
                objectFit: 'cover',
                border: '3px solid rgba(255,255,255,0.7)',
                justifySelf: { xs: 'start', md: 'end' },
              }}
            />
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' },
              gap: 1.5,
              mt: 5,
            }}
          >
            {[
              ['34+ services', 'In production now, across both platforms'],
              ['27+ Polaris services', 'One repository each. Core platform I run today.'],
              ['7 DataStudio apps', 'Including the PolarisFE UI. Product on Polaris.'],
            ].map(([title, detail]) => (
              <Box key={title} sx={{ border: '1px solid rgba(255,255,255,0.28)', borderRadius: 1, px: 2, py: 1.5, bgcolor: 'rgba(255,255,255,0.08)' }}>
                <Typography sx={{ fontWeight: 700, fontSize: '1.15rem' }}>{title}</Typography>
                <Typography sx={{ opacity: 0.88, fontSize: '0.92rem' }}>{detail}</Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Box id="next" sx={{ bgcolor: '#f3f8f9', borderLeft: '3px solid #0e4c5c', px: 2.5, py: 2 }}>
          <Typography>
            <Box component="span" sx={{ color: 'primary.main', fontWeight: 700 }}>Next role. </Box>
            Staff Software Engineer or Senior Systems Engineer on a product data platform.
            Python, FastAPI, Polars, PostgreSQL, DynamoDB, and AWS used asynchronously — SQS, Lambda, ECS, Step Functions, EventBridge — with React and Redux where the product needs a surface.
          </Typography>
        </Box>
      </Container>

      <Container id="systems" maxWidth="lg" sx={{ pb: 6 }}>
        <Typography sx={sectionTitleSx}>Systems</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.2fr 1.15fr 0.9fr' }, gap: 2 }}>
          {systems.map((system) => (
            <Box key={system.title} sx={{ bgcolor: 'white', border: '1px solid #d7dee6', borderTop: '3px solid #0e4c5c', borderRadius: 1, p: 2.25 }}>
              <Typography sx={{ fontSize: '0.72rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: '#146275', fontWeight: 700 }}>
                {system.kicker}
              </Typography>
              <Typography variant="h3" sx={{ fontSize: '1.35rem', mt: 0.5 }}>{system.title}</Typography>
              <Typography color="text.secondary" sx={{ fontSize: '0.9rem', mt: 0.25 }}>{system.when}</Typography>
              <Typography sx={{ mt: 1.25, fontSize: '0.98rem' }}>{system.body}</Typography>
              <Stack direction="row" useFlexGap flexWrap="wrap" spacing={0.75} sx={{ mt: 1.5 }}>
                {system.chips.map((chip) => (
                  <Chip key={chip} label={chip} size="small" sx={{ bgcolor: '#e6f0f3', color: '#0e3d4a', fontWeight: 700 }} />
                ))}
              </Stack>
            </Box>
          ))}
        </Box>

        <Typography sx={{ ...sectionTitleSx, mt: 5 }}>How Polaris is cut</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2 }}>
          {cuts.map((cut) => (
            <Box key={cut.title} sx={{ borderTop: '2px solid #0e4c5c', pt: 1 }}>
              <Typography sx={{ fontWeight: 700 }}>{cut.title}</Typography>
              <Typography color="text.secondary" sx={{ fontSize: '0.95rem', mt: 0.5 }}>{cut.body}</Typography>
            </Box>
          ))}
        </Box>
      </Container>

      <Box id="stack" sx={{ bgcolor: 'white', borderTop: '1px solid #d7dee6', borderBottom: '1px solid #d7dee6', py: 6 }}>
        <Container maxWidth="lg">
          <Typography sx={sectionTitleSx}>Stack I am hired for</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 3 }}>
            {stackGroups.map((group) => (
              <Box key={group.title}>
                <Typography sx={{ fontWeight: 700, mb: 1 }}>{group.title}</Typography>
                <Stack direction="row" useFlexGap flexWrap="wrap" spacing={0.75}>
                  {group.items.map((item) => (
                    <Chip key={item} label={item} variant="outlined" size="small" sx={{ borderColor: '#d7dee6' }} />
                  ))}
                </Stack>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      <Container id="experience" maxWidth="lg" sx={{ py: 6 }}>
        <Typography sx={sectionTitleSx}>Before this Creditsafe stretch</Typography>
        <Stack spacing={2}>
          {earlier.map((job) => (
            <Box key={job.title} sx={{ bgcolor: 'white', border: '1px solid #d7dee6', borderRadius: 1, p: 2.25 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                <Typography sx={{ fontWeight: 700, fontSize: '1.05rem' }}>{job.title}</Typography>
                <Typography color="text.secondary">{job.when}</Typography>
              </Box>
              <Typography sx={{ mt: 1 }}>{job.body}</Typography>
              <Typography color="text.secondary" sx={{ mt: 1, fontSize: '0.92rem' }}>{job.stack}</Typography>
            </Box>
          ))}
        </Stack>
      </Container>

      <Box id="contact" sx={{ bgcolor: '#0e4c5c', color: 'white', py: 6 }}>
        <Container maxWidth="lg">
          <Typography sx={{ ...sectionTitleSx, color: 'rgba(255,255,255,0.75)' }}>Contact</Typography>
          <Typography variant="h2" sx={{ fontSize: '2rem', mb: 2 }}>
            Srivathsa Kavirayuni
          </Typography>
          <Stack spacing={1}>
            <Typography>
              <Box component="a" href={`mailto:${contact.email}`} sx={{ color: 'inherit' }}>{contact.email}</Box>
            </Typography>
            <Typography>
              <Box component="a" href={contact.phoneHref} sx={{ color: 'inherit' }}>{contact.phone}</Box>
            </Typography>
            <Typography>
              <Box component="a" href={contact.linkedin} target="_blank" rel="noopener noreferrer" sx={{ color: 'inherit' }}>
                LinkedIn
              </Box>
            </Typography>
            <Typography>
              <Box component="a" href={contact.site} sx={{ color: 'inherit' }}>srivathsakavirayuni.techiegra.com</Box>
            </Typography>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
};

export default Home;
